import { ResGetUsers, UserRoles } from "@gettactic/api";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTacticHooks } from "../../providers/TacticHooksProvider";

export function useUserListPaginated(
  query: string | null,
  limit: number,
  filters: { [idx: string]: string | null } = {},
  roles: UserRoles[] = []
) {
  const { api } = useTacticHooks();
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== null && typeof v !== "undefined") {
      params.set(k, v);
    }
  }
  if (query && query.trim() !== "") {
    params.set("search", query);
  }
  params.set("limit", limit.toString());
  roles.forEach((role) => {
    params.append("role", role);
  });
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
  } = useInfiniteQuery<ResGetUsers | null>({
    queryKey: ["organizations.usersPaginated", params.toString()],
    queryFn: async ({ pageParam: cursor }) => {
      if (cursor) {
        params.set("cursor", cursor as string);
      }
      const res = await api.client.organizations.usersPaginated(
        params
      );
      return res.result;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) =>
      (lastPage?.elements.length ?? 0) < limit
        ? undefined
        : (lastPage?.cursor ?? undefined)
  });

  const allPages = useMemo(
    () =>
      (data?.pages || [])
        .map((x) => x?.elements ?? [])
        .reduce((arr, els) => {
          return arr.concat(els);
        }, []),
    [data]
  );

  return {
    data,
    allPages,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
  };
}
